import { useState, useEffect, useMemo, useCallback } from "react";
import { TroquinhoCalendar } from "@/components/shared/TroquinhoCalendar";
import { motion, AnimatePresence } from "framer-motion";
import { 
  TrendingUp, TrendingDown, Wallet, Calendar, 
  ChevronLeft, ChevronRight, Users, User, Copy, 
  Check, MessageCircle, X, ChevronDown, Trophy,
  Sparkles, AlertTriangle, FileText, ListChecks
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { parseISO, format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { useOracle } from "@/contexts/OracleContext";
import { subscribeToTransactions } from "@/services/transactionsService";
import { subscribeToConnections } from "@/services/connectionsService";

// Months in Portuguese
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

// Category colors
const CATEGORY_COLORS = {
  "Salário": "bg-emerald-500",
  "Freelance": "bg-teal-500",
  "Investimentos": "bg-cyan-500",
  "Presentes": "bg-pink-500",
  "Outros": "bg-slate-500",
  "Alimentação": "bg-orange-500",
  "Moradia": "bg-blue-500",
  "Transporte": "bg-amber-500",
  "Saúde": "bg-red-500",
  "Educação": "bg-indigo-500",
  "Lazer": "bg-purple-500",
  "Vestuário": "bg-rose-500",
  "Mercado": "bg-lime-500",
  "Delivery": "bg-orange-400",
  "Assinaturas": "bg-violet-500",
  "Contas": "bg-gray-500",
};

// Format currency
const formatCurrency = (value) => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

// Get category color
const getCategoryColor = (category) => {
  // 1. Blindagem: Se a categoria for nula, indefinida ou não for texto, 
  // retorna a cor padrão e para de reclamar.
  if (!category || typeof category !== 'string') return "bg-slate-400";

  // 2. Verifica se é uma das cores oficiais que mapeamos
  if (CATEGORY_COLORS[category]) return CATEGORY_COLORS[category];
  
  // 3. Para categorias customizadas, gera uma cor baseada no nome
  const genericColors = [
    "bg-sky-500", "bg-violet-500", "bg-emerald-500", 
    "bg-rose-500", "bg-amber-500", "bg-fuchsia-500"
  ];
  
  const index = category.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return genericColors[index % genericColors.length];
};

export default function Reports() {
  const { user } = useAuth();
  
  // Period state
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  // Filter state
  const [selectedParticipant, setSelectedParticipant] = useState("all");

  const { isOracleActive } = useOracle();

  // Sincroniza o calendário com o Modo Oráculo
  useEffect(() => {
    const hoje = new Date();
    if (isOracleActive) {
      const dataFutura = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
      setCurrentMonth(dataFutura.getMonth());
      setCurrentYear(dataFutura.getFullYear());
    } else {
      setCurrentMonth(hoje.getMonth());
      setCurrentYear(hoje.getFullYear());
    }
  }, [isOracleActive]);

  // Data state
  const [transactions, setTransactions] = useState([]);
  const [connections, setConnections] = useState({ people: [], cards: [], banks: [] });
  const [isLoading, setIsLoading] = useState(true);
  
  // UI state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategorySheet, setShowCategorySheet] = useState(false);
  const [copiedType, setCopiedType] = useState(null);

  // Subscribe to data (Conexão com Firebase)
  useEffect(() => {
    if (!user) return;
    
    setIsLoading(true);
    const unsubscribeTransactions = subscribeToTransactions(
      user.uid, 
      currentMonth, 
      currentYear, 
      (data) => {
        setTransactions(data);
        setIsLoading(false);
      }
    );

    const unsubscribeConnections = subscribeToConnections(user.uid, (data) => {
      setConnections(data);
    });

    return () => {
      unsubscribeTransactions();
      unsubscribeConnections();
    };
  }, [user, currentMonth, currentYear]);

  // Filter transactions by period
  const periodTransactions = useMemo(() => {
    return transactions.filter(t => {
      // Usando parseISO para garantir que o fuso horário não mude o mês da transação
      const tDate = parseISO(t.dueDate || t.date);
      return tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear;
    });
  }, [transactions, currentMonth, currentYear]);

  // Filter by participant
  const filteredTransactions = useMemo(() => {
    if (selectedParticipant === "all") {
      return periodTransactions;
    }
    
    if (selectedParticipant === "me") {
      // Transactions where I'm the owner or I participated
      return periodTransactions.filter(t => 
        !t.participants?.length || (t.iParticipated !== false && t.myShare > 0)
      );
    }
    
    // Specific participant
    return periodTransactions.filter(t => 
      t.participants?.some(p => p.id === selectedParticipant)
    );
  }, [periodTransactions, selectedParticipant]);

  // Calculate KPIs
  const kpis = useMemo(() => {
    const useShare = selectedParticipant !== "all";
    
    const income = filteredTransactions
      .filter(t => t.type === "income")
      .reduce((sum, t) => sum + (selectedParticipant === "all" ? t.amount : (selectedParticipant === "me" ? (t.myShare ?? t.amount) : (t.participants?.find(p => p.id === selectedParticipant)?.share ?? 0))), 0);
    
    const expenses = filteredTransactions
  .filter(t => t.type === "expense") // Ignora TPD para não sujar o gráfico de gastos reais
  .reduce((sum, t) => sum + (selectedParticipant === "all" ? t.amount : (selectedParticipant === "me" ? (t.myShare ?? t.amount) : (t.participants?.find(p => p.id === selectedParticipant)?.share ?? 0))), 0);

    return {
      income,
      expenses,
      balance: income - expenses
    };
  }, [filteredTransactions, selectedParticipant]);

  // Calculate categories breakdown
  const categoriesBreakdown = useMemo(() => {
    const useShare = selectedParticipant !== "all";
    const categoryMap = new Map();
    
    filteredTransactions
      .filter(t => t.type === "expense") // Só vira "inimigo" quem realmente gasta o dinheiro do grupo/casa
      .forEach(t => {
        const category = t.category || "Outros";
        const value = selectedParticipant === "all" ? t.amount : (selectedParticipant === "me" ? (t.myShare ?? t.amount) : (t.participants?.find(p => p.id === selectedParticipant)?.share ?? 0));
        categoryMap.set(category, (categoryMap.get(category) || 0) + value);
      });
    
    const total = Array.from(categoryMap.values()).reduce((sum, v) => sum + v, 0);
    
    return Array.from(categoryMap.entries())
      .map(([name, amount]) => ({
        name,
        amount,
        percentage: total > 0 ? Math.round((amount / total) * 100) : 0,
        color: getCategoryColor(name)
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [filteredTransactions, selectedParticipant]);

  // Get transactions for selected category
  const categoryTransactions = useMemo(() => {
    if (!selectedCategory) return [];
    const useShare = selectedParticipant !== "all";
    
    return filteredTransactions
      .filter(t => t.type === "expense" && t.category === selectedCategory)
      .map(t => ({
        ...t,
        displayAmount: selectedParticipant === "all" ? t.amount : (selectedParticipant === "me" ? (t.myShare ?? t.amount) : (t.participants?.find(p => p.id === selectedParticipant)?.share ?? 0))
      }));
  }, [filteredTransactions, selectedCategory, selectedParticipant]);

  // Calculate "Enemies of End of Month" ranking (only for "all" filter)
  const enemiesRanking = useMemo(() => {
    if (selectedParticipant !== "all") return [];
    
    const participantTotals = new Map();
    participantTotals.set("me", { name: "Eu", total: 0 });
    
    periodTransactions
      .filter(t => t.type === "expense") // Mostra apenas o que saiu do bolso de fato
      .forEach(t => {
        // Add my share (usando o valor real salvo no banco)
        if (t.iParticipated !== false && t.myShare !== undefined) {
          const current = participantTotals.get("me");
          participantTotals.set("me", { ...current, total: current.total + t.myShare });
        }
        
        // Add participants' shares
        t.participants?.forEach(p => {
          if (!participantTotals.has(p.id)) {
            participantTotals.set(p.id, { name: p.name, total: 0 });
          }
          const current = participantTotals.get(p.id);
          participantTotals.set(p.id, { ...current, total: current.total + (p.share || 0) });
        });
      });
    
    return Array.from(participantTotals.entries())
      .map(([id, data]) => ({ id, ...data }))
      .filter(p => p.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);
  }, [periodTransactions, selectedParticipant]);

  // Open category drill-down
  const openCategorySheet = (category) => {
    setSelectedCategory(category);
    setShowCategorySheet(true);
  };

  // Copy to clipboard
  const copyToClipboard = async (type) => {
    let text = "";
    
    if (type === "simple") {
      text = generateSimpleReport();
    } else {
      text = generateCompleteReport();
    }
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedType(type);
      toast.success("Copiado para a área de transferência!");
      setTimeout(() => setCopiedType(null), 2000);
    } catch (error) {
      toast.error("Erro ao copiar. Tente novamente.");
    }
  };

  // Generate simple report
  const generateSimpleReport = () => {
    let report = `📊 RELATÓRIO TROQUINHO - ${MONTHS[currentMonth]} ${currentYear}\n\n`;
    report += `💰 RESUMO\n`;
    report += `✅ Entrou: R$ ${formatCurrency(kpis.income)}\n`;
    report += `❌ Saiu: R$ ${formatCurrency(kpis.expenses)}\n`;
    report += `📈 Saldo: R$ ${formatCurrency(kpis.balance)}\n\n`;
    
    if (categoriesBreakdown.length > 0) {
      report += `📋 DESPESAS POR CATEGORIA\n`;
      categoriesBreakdown.forEach(cat => {
        report += `• ${cat.name}: R$ ${formatCurrency(cat.amount)} (${cat.percentage}%)\n`;
      });
      report += `\n`;
    }
    
    return report;
  };

  // Generate complete report
  const generateCompleteReport = () => {
    let report = generateSimpleReport();
    report += `\n📝 DETALHAMENTO POR CATEGORIA\n`;
    
    categoriesBreakdown.forEach(cat => {
      report += `\n▸ ${cat.name.toUpperCase()}\n`;
      const catTransactions = filteredTransactions.filter(t => 
        (t.type === "expense" || t.type === "te_pago_depois") && t.category === cat.name
      );
      catTransactions.forEach(t => {
        const value = selectedParticipant === "all"
          ? t.amount
          : (selectedParticipant === "me"
              ? (t.myShare ?? t.amount)
              : (t.participants?.find(p => p.id === selectedParticipant)?.share ?? 0));
        const parcelaTag = t.seriesTotalMonths > 1 ? ` (${t.seriesIndex}/${t.seriesTotalMonths})` : "";
        report += `  - ${t.description}${parcelaTag}: R$ ${formatCurrency(value)} (${format(parseISO(t.date), "dd/MM/yyyy")})\n`;
      });
    });

    return report;
  };

  // Medal emoji helper
  const getMedal = (index) => {
    const medals = ["🥇", "🥈", "🥉"];
    return medals[index] || "";
  };

  return (
<div className={`min-h-screen pb-32 transition-colors duration-500 ${isOracleActive ? 'bg-indigo-50/50' : 'bg-slate-50'}`}>
  <div className="max-w-lg mx-auto px-5 py-6">
    
    {/* Header Centralizado */}
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8 text-center"
    >
      <h1 className={`text-2xl font-black tracking-tight mb-1 ${isOracleActive ? 'text-indigo-900' : 'text-slate-900'}`}>
        Relatórios
      </h1>
      <p className="text-slate-500 text-sm italic">
        Entender seus números é libertador.
      </p>
    </motion.header>

      {/* Navegador de Período Centralizado */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="flex justify-center mb-8"
    >
      <TroquinhoCalendar 
        currentMonth={currentMonth} 
        currentYear={currentYear} 
        onPeriodChange={(month, year) => {
          setCurrentMonth(month);
          setCurrentYear(year);
        }} 
      />
    </motion.div>

        {/* Participant Filter Chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-5"
        >
          <div className="flex flex-wrap gap-2 pb-4 justify-center">
            <Button
              variant={selectedParticipant === "all" ? "default" : "outline"}
              size="sm"
              className="rounded-full whitespace-nowrap"
              onClick={() => setSelectedParticipant("all")}
              data-testid="filter-all"
            >
              <Users className="w-4 h-4 mr-1" />
              Todos
            </Button>
            <Button
              variant={selectedParticipant === "me" ? "default" : "outline"}
              size="sm"
              className="rounded-full whitespace-nowrap"
              onClick={() => setSelectedParticipant("me")}
              data-testid="filter-me"
            >
              <User className="w-4 h-4 mr-1" />
              Eu
            </Button>
            
            {connections.people.length > 4 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full">
                    <Users className="w-4 h-4 mr-1" />
                    Pessoas
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {connections.people.map(person => (
                    <DropdownMenuItem 
                      key={person.id}
                      onClick={() => setSelectedParticipant(person.id)}
                    >
                      {person.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              connections.people.map(person => (
                <Button
                  key={person.id}
                  variant={selectedParticipant === person.id ? "default" : "outline"}
                  size="sm"
                  className="rounded-full whitespace-nowrap"
                  onClick={() => setSelectedParticipant(person.id)}
                  data-testid={`filter-${person.id}`}
                >
                  {person.name}
                </Button>
              ))
            )}
          </div>
        </motion.div>

{/* KPI Cards Otimizados para Mobile */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.15 }}
  className="grid grid-cols-2 gap-3 mb-5"
>
  {/* Income - Lado Esquerdo */}
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col items-start" data-testid="kpi-income">
    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center mb-2">
      <TrendingUp className="w-4 h-4 text-emerald-600" />
    </div>
    <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Entradas</p>
    <p className="text-lg font-black text-emerald-600 truncate w-full">
      R$ {formatCurrency(kpis.income)}
    </p>
  </div>

  {/* Expenses - Lado Direito */}
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col items-start" data-testid="kpi-expenses">
    <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center mb-2">
      <TrendingDown className="w-4 h-4 text-rose-600" />
    </div>
    <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Saídas</p>
    <p className="text-lg font-black text-rose-600 truncate w-full">
      R$ {formatCurrency(kpis.expenses)}
    </p>
  </div>

  {/* Balance - Embaixo (Ocupando as 2 colunas) */}
  <div className={`col-span-2 bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex items-center justify-between transition-all duration-500 ${isOracleActive ? 'ring-2 ring-purple-500/20' : ''}`} data-testid="kpi-balance">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${kpis.balance >= 0 ? 'bg-indigo-100' : 'bg-amber-100'} flex items-center justify-center shadow-sm`}>
        <Wallet className={`w-5 h-5 ${kpis.balance >= 0 ? 'text-indigo-600' : 'text-amber-600'}`} />
      </div>
      <div>
        <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Saldo do Período</p>
        <p className={`text-xl font-black ${kpis.balance >= 0 ? 'text-indigo-600' : 'text-amber-600'}`}>
          R$ {formatCurrency(kpis.balance)}
        </p>
      </div>
    </div>
    {isOracleActive && (
      <Badge className="bg-purple-600 text-white border-none animate-pulse">Oráculo</Badge>
    )}
  </div>
</motion.div>


      {/* Balance Microcopy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          /* Classe dinâmica: Roxo se Oráculo, senão Verde/Amarelo */
          className={`rounded-[20px] p-4 mb-5 transition-colors duration-500 ${
            isOracleActive 
              ? 'bg-purple-100 border border-purple-200' 
              : kpis.balance >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'
          }`}
        >
          <p className={`text-sm flex items-center gap-2 ${
            isOracleActive 
              ? 'text-purple-800' 
              : kpis.balance >= 0 ? 'text-emerald-700' : 'text-amber-700'
          }`}>
            {isOracleActive ? (
              <>
                <Sparkles className="w-4 h-4 text-purple-600" />
                <strong>Modo Oráculo:</strong> Você está vendo o futuro. Use essa visão para ajustar o presente!
              </>
            ) : kpis.balance >= 0 ? (
              <>
                <Sparkles className="w-4 h-4" />
                Saldo positivo! Um passo de cada vez rumo à liberdade.
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4" />
                Atenção, os números estão querendo conversar sério com você.
              </>
            )}
          </p>
        </motion.div>

        {/* Categories Breakdown */}
        {categoriesBreakdown.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-5"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-3">Despesas por Categoria</h2>
            <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100">
              {/* Visual bar */}
              <div className="h-6 rounded-full overflow-hidden flex mb-5">
                {categoriesBreakdown.map((cat, index) => (
                  <motion.div
                    key={cat.name}
                    initial={{ width: 0 }}
                    animate={{ width: `${cat.percentage}%` }}
                    transition={{ delay: 0.3 + index * 0.05, duration: 0.4 }}
                    className={`${cat.color} first:rounded-l-full last:rounded-r-full cursor-pointer hover:opacity-80 transition-opacity`}
                    onClick={() => openCategorySheet(cat.name)}
                  />
                ))}
              </div>

              {/* Category list */}
              <div className="space-y-3">
                {categoriesBreakdown.map((cat, index) => (
                  <motion.div
                    key={cat.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35 + index * 0.03 }}
                    className="flex items-center justify-between cursor-pointer hover:bg-slate-50 rounded-xl p-2 -m-2 transition-colors"
                    onClick={() => openCategorySheet(cat.name)}
                    data-testid={`category-${cat.name}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${cat.color}`} />
                      <span className="font-medium text-slate-700">{cat.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">{cat.percentage}%</span>
                      <span className="font-bold text-slate-900">R$ {formatCurrency(cat.amount)}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Category Drill-down Sheet */}
        <Sheet open={showCategorySheet} onOpenChange={setShowCategorySheet}>
          <SheetContent side="bottom" className="rounded-t-[32px] max-h-[70vh]">
            <SheetHeader className="pb-4">
              <SheetTitle className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${getCategoryColor(selectedCategory)}`} />
                {selectedCategory}
              </SheetTitle>
            </SheetHeader>
            <div className="space-y-3 overflow-y-auto max-h-[50vh]">
              {categoryTransactions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nenhum lançamento nesta categoria</p>
              ) : (
                categoryTransactions.map((t, index) => (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                  >
                                        <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{t.description}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p className="text-sm text-slate-500">
                          {(t.date || t.dueDate) ? format(parseISO(t.date || t.dueDate), "dd/MM/yyyy") : "--"}
                        </p>
                        {t.seriesTotalMonths > 1 && (
                          <Badge
                            variant="secondary"
                            className={`text-[9px] h-4 border-none font-black uppercase ${
                              t.seriesIndex === t.seriesTotalMonths
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {t.seriesIndex}/{t.seriesTotalMonths}
                            {t.seriesIndex === t.seriesTotalMonths && " · Última 🎉"}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="font-bold text-rose-600 flex-shrink-0 ml-3">R$ {formatCurrency(t.displayAmount)}</p>
                  </motion.div>
                ))
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Enemies of End of Month (only for "all" filter) */}
        <AnimatePresence>
          {selectedParticipant === "all" && enemiesRanking.length > 1 && (
            <motion.section
              initial={{ opacity: 0, y: 20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-5"
            >
              <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Inimigos do Fim do Mês
              </h2>
              <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100">
                <p className="text-xs text-slate-500 mb-4">Quem mais gastou neste mês (cada um com sua parte)</p>
                <div className="space-y-3">
                  {enemiesRanking.map((person, index) => (
                    <motion.div
                      key={person.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{getMedal(index)}</span>
                        <span className="font-medium text-slate-700">{person.name}</span>
                      </div>
                      <span className="font-bold text-rose-600">R$ {formatCurrency(person.total)}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* Export Buttons */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-3"
        >
          <h2 className="text-lg font-bold text-slate-900 mb-3">Exportar Relatório</h2>
          
          <Button
            variant="outline"
            className="w-full h-14 rounded-full font-semibold justify-start px-6"
            onClick={() => copyToClipboard("simple")}
            data-testid="export-simple"
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center mr-4">
              <ListChecks className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="text-left flex-1">
              <p className="font-semibold text-slate-900">Resumo Simplificado</p>
              <p className="text-xs text-slate-500">Totais e acerto de contas</p>
            </div>
            {copiedType === "simple" ? (
              <Check className="w-5 h-5 text-emerald-500" />
            ) : (
              <Copy className="w-5 h-5 text-slate-400" />
            )}
          </Button>

          <Button
            variant="outline"
            className="w-full h-14 rounded-full font-semibold justify-start px-6"
            onClick={() => copyToClipboard("complete")}
            data-testid="export-complete"
          >
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mr-4">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
            <div className="text-left flex-1">
              <p className="font-semibold text-slate-900">Relatório Completo</p>
              <p className="text-xs text-slate-500">Detalhamento por categoria</p>
            </div>
            {copiedType === "complete" ? (
              <Check className="w-5 h-5 text-emerald-500" />
            ) : (
              <Copy className="w-5 h-5 text-slate-400" />
            )}
          </Button>
        </motion.section>

        {/* Empty State */}
        {!isLoading && filteredTransactions.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Nenhum lançamento</h3>
            <p className="text-slate-500 text-sm">
              Nada por aqui. Ou você está sendo muito econômico ou esqueceu de anotar aquela pizza de ontem.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
